import { cn } from 'cn';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Skeleton } from '@/components/ui/skeleton';
import { buttonVariants } from '@/components/ui/button';

/**
 * The phone adaptation of a queue row (docs/ux/shell.md): reference and
 * patient, the chip, due and days, up to three label-value pairs, a
 * full-width 44 px Open. Cards are articles with the reference as heading.
 */
export function MatterCardList({ children, state, className }: { children: ReactNode; state: 'loading' | 'ready'; className?: string }) {
  return (
    <div data-slot="matter-card-list" className={cn('flex flex-col gap-3', className)}>
      {state === 'loading' ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-xl" aria-hidden="true" />) : children}
    </div>
  );
}

export type MatterCardProps = {
  href: string;
  reference: string;
  patient: string;
  chip: ReactNode;
  due: ReactNode;
  days: ReactNode;
  /**
   * Label-value pairs. A value that reads on its own (a doctor, a referrer, a
   * type) shows without its label; a bare count shows with the label, or set
   * `showLabel` (docs/ux/dr.md, docs/ux/qa.md section 4).
   */
  pairs: Array<{ label: string; value: ReactNode; showLabel?: boolean }>;
  flags?: ReactNode;
  overdue?: boolean;
  /** The button's verb: "Open" (default), or "View" where the destination is read only for this persona (docs/ux/qa.md). */
  action?: string;
};

export function MatterCard({ href, reference, patient, chip, due, days, pairs, flags, overdue, action = 'Open' }: MatterCardProps) {
  return (
    <article
      data-slot="matter-card"
      data-state={overdue ? 'overdue' : 'default'}
      aria-labelledby={`card-${reference}`}
      className={cn('flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper p-4', overdue && 'border-l-[3px] border-l-beam-error')}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 id={`card-${reference}`} className="tabular text-sm font-semibold">{reference}</h2>
        <span className="truncate text-sm">{patient}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">{chip}</div>
      <div className="flex items-baseline justify-between text-[13px]">
        <span>Due {due}</span>
        <span className="flex items-baseline gap-1">{days}<span className="text-beam-text-muted">days</span></span>
      </div>
      {pairs.length > 0 ? (
        <p className="text-[13px] text-beam-text-muted">
          {pairs.map((p, i) => {
            const labelled = p.showLabel || typeof p.value === 'number' || (typeof p.value === 'string' && /^\d+$/.test(p.value));
            return (
              <span key={p.label}>
                {i > 0 ? ' · ' : ''}
                {labelled ? <>{p.label}<span className="sr-only">:</span> <span className="font-medium text-foreground">{p.value}</span></> : <><span className="sr-only">{p.label}: </span>{p.value}</>}
              </span>
            );
          })}
        </p>
      ) : null}
      {flags ? <div className="flex flex-wrap gap-3">{flags}</div> : null}
      <Link to={href} className={cn(buttonVariants({ size: 'lg', variant: action === 'Open' ? 'default' : 'outline' }), 'mt-1 h-11 w-full')} aria-label={`${action} ${reference}`}>
        {action}
      </Link>
    </article>
  );
}
